<script setup lang="ts">
import type { GoogleAddressLocation, GoogleAddressPrediction, GoogleAddressResult, GoogleAddressSuggestion } from '@global-types/googleAddress.ts';
import Logger from '@tools/logger/logger';
import { ref, onMounted, watch, computed } from 'vue';
import IconXCircleFill from '@icons/ph_x-circle-fill.svg';
import Icon from '@components/Icon.vue';
import { trans } from '@elements/translation';
import { directive as clickAway } from 'vue3-click-away';

type PlaceResult = GoogleAddressResult | null;

defineOptions({ directives: { clickAway } });

const props = withDefaults(defineProps<{
  modelValue: PlaceResult | null,
  placeholder?: string,
}>(), {
  modelValue: null,
});

const emit = defineEmits<{(e: 'update:modelValue', v: PlaceResult): void, (e: 'ready'): void, (e: 'error', v: boolean): void }>();

const inputEl = ref<HTMLInputElement | null>(null);
const query = ref<string>(props.modelValue?.text ?? '');
const openList = ref<boolean>(false);
const suggestions = ref<GoogleAddressSuggestion[]>([]);

const error = computed<boolean>(() => {
  if (query.value.length === 0) {
    return false;
  }
  if (props.modelValue?.placeId) {
    return false;
  }
  return !openList.value;
});

const apiKey = window.GOOGLE_API_KEY;

const open = (): void => {
  openList.value = true;
};

const clear = (): void => {
  emit('update:modelValue', null);
  suggestions.value = [];
  query.value = '';
  openList.value = false;
};

const fetchSuggestions = async (): Promise<void> => {
  const input = query.value;
  if (!input) {
    suggestions.value = [];
    return;
  }

  try {
    const response = await fetch('https://places.googleapis.com/v1/places:autocomplete', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': apiKey,
        'X-Goog-FieldMask': 'suggestions.placePrediction.placeId,suggestions.placePrediction.text.text',
      },
      body: JSON.stringify({
        input,
        includedRegionCodes: [ 'FR' ],
        languageCode: 'fr',
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const json = await response.json();

    const items: GoogleAddressSuggestion[] = (json?.suggestions ?? [])
      .map((result: { placePrediction: GoogleAddressPrediction }) => result.placePrediction)
      .filter((prediction: GoogleAddressPrediction) => !!prediction.placeId)
      .map((prediction: GoogleAddressPrediction) => ({
        placeId: prediction.placeId,
        text: prediction.text?.text ?? '',
      } as GoogleAddressSuggestion))
      .filter((suggestion: GoogleAddressSuggestion) => suggestion.placeId && suggestion.text);

    suggestions.value = items.slice(0, 5);
  } catch (err) {
    Logger.error('Autocomplete error', err);
    suggestions.value = [];
  }
};

const handleInputChange = (): void => {
  open();
  fetchSuggestions();
  emit('update:modelValue', null);
};

const close = (): void => {
  openList.value = false;
};

const select = async (suggestion: GoogleAddressSuggestion): Promise<void> => {
  try {
    const url = `https://places.googleapis.com/v1/places/${encodeURIComponent(suggestion.placeId)}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': apiKey,
        'X-Goog-FieldMask': 'location',
      },
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    const place = await response.json();
    const location = place.location as GoogleAddressLocation;
    emit('update:modelValue', { ...suggestion, location } as GoogleAddressResult);
  } catch (err) {
    Logger.error('Place Details error', err);
    emit('update:modelValue', null);
  } finally {
    query.value = suggestion.text;
    close();
  }
};

onMounted((): void => {
  emit('ready');
});

watch(() => props.modelValue, (value): void => {
  if (!value) {
    return;
  }
  query.value = value.text || query.value;
});

watch(error, (value): void => {
  emit('error', value);
});
</script>

<template>
  <div
    class="input-text input-address"
    :class="{ error }"
    @keydown.esc="close"
  >
    <input
      ref="inputEl"
      v-model="query"
      class="input-address__input"
      type="text"
      :placeholder="placeholder"
      autocomplete="off"
      @input="handleInputChange"
      @focus="open"
    >

    <div class="input-address__clear" @click="clear">
      <Icon :svg="IconXCircleFill" :width="24" :height="24" />
    </div>

    <ul
      v-if="openList && suggestions.length"
      v-click-away="close"
      class="input-address__suggestions address-suggestions"
    >
      <li
        v-for="(suggestion, index) in suggestions"
        :key="suggestion.placeId || index"
        class="address-suggestions__item"
        @click="select(suggestion)"
      >
        {{ suggestion.text }}
      </li>
    </ul>
    <label v-if="error" class="error">{{ trans('selection_item.custom_marker.form.address.error') }}</label>
  </div>
</template>

<style scoped lang="scss">
@use '@styles/variables' as *;
@use '@styles/mixins' as *;

.input-address {
  &__clear {
    display: flex;
    position: absolute;
    width: rem(48);
    height: rem(48);
    right: 0;
    top: 0;
    justify-content: center;
    align-items: center;
    cursor: pointer;
  }

  &__suggestions, .address-suggestions {
    position: absolute;
    background: $colorWhite;
    padding-left: 0;
    margin: 0;
    z-index: 5;
    top: rem(54);
    border-radius: rem(4);
    overflow: hidden;
    max-width: 100%;
    // weird but inherited from inputLocation.scss
    border: 1px solid rgba(139, 146, 169, 0.2);
    box-shadow: 0 rem(2) rem(8) 0 rgba(26, 32, 53, 0.15);

    &__item {
      @include fontBody;
      width: 100%;
      display: flex;
      padding: rem(12);
      color: $colorBlack;
      cursor: pointer;
      list-style: none;
      margin: 0;
      min-height: rem(20);
      border-top: 0;
      background-color: $colorWhite;
      justify-content: space-between;
      line-height: rem(20);

      &:hover {
        background: $newBackgroundColor;
        color: $colorBlack;
      }
    }
  }
}

@media screen and (min-width: $breakHeaderTabletLow) {
  .address-suggestions {
    width: rem(322);
    left: rem(-2);
    top: rem(40);

    &__item {
      padding: rem(13);
    }
  }
}

</style>
